export default class Rectangle {
  constructor({ top, bottom, left, right, width, height }, id) {
    this.id = id

    const def = (value, def) => {
      if (value !== undefined) return value
      if (isNaN(def)) return 0
      return def
    }

    this._top = def(top, bottom - height)
    this._bottom = def(bottom, this._top + height)
    this._left = def(left, right - width)
    this._right = def(right, this._left + width)

    this._width = this._right - this._left || width || 0
    this._height = this._bottom - this._top || height || 0
  }

  get top() {
    return this._top
  }
  get y() {
    return this._top
  }
  get bottom() {
    return this._bottom
  }
  get left() {
    return this._left
  }
  get x() {
    return this._left
  }
  get right() {
    return this._right
  }
  get width() {
    return this._width
  }
  get height() {
    return this._height
  }

  set top(top) {
    this._height = this._height - (top - this._top)
    this._top = top
  } 
  set y(y) {
    this._top = y
    this._bottom = this._top + this._height
  }
  set bottom(bottom) {
    this._height = this._height + (bottom - this._bottom)
    this._bottom = bottom
  }
  set left(left) {
    this._width = this._width - (left - this._left)
    this._left = left
  }
  set x(x) {
    this._left = x
    this._right = this._left + this._width
  }
  set right(right) {
    this._width = this._width + (right - this._right)
    this._right = right
  }
  set width(width) {
    this._right = this._right + (width - this._width)
    this._width = width
  }
  set height(height) {
    this._bottom = this._bottom + (height - this._height)
    this._height = height
  }

  overlaps(rectangle) {
    return !(
      this.right < rectangle.left ||
      rectangle.right < this.left ||
      this.bottom < rectangle.top ||
      rectangle.bottom < this.top
    )
  }

  static combine(rectangles) {
    let { top, bottom, left, right } = rectangles[0]

    rectangles.slice(1).forEach(rectangle => {
      top = Math.min(top, rectangle.top)
      bottom = Math.max(bottom, rectangle.bottom)
      left = Math.min(left, rectangle.left)
      right = Math.max(right, rectangle.right)
    })

    return new Rectangle({ top, bottom, left, right })
  }

  getDelta(other) {
    other = new Rectangle(other)
    return new Rectangle({
      top: other.top - this.top,
      left: other.left - this.left,
      width: other.width - this.width,
      height: other.height - this.height
    })
  }

  withDelta(delta) {
    delta = new Rectangle(delta)
    return new Rectangle({
      top: this.top + delta.top,
      left: this.left + delta.left,
      right: this.right + delta.right,
      bottom: this.bottom + delta.bottom
    })
  }

  scale(scale) {
    return new Rectangle({
      top: this.top * scale,
      left: this.left * scale,
      right: this.right * scale,
      bottom: this.bottom * scale
    })
  }

  /**
   * Return a copy of this rectangle cropped to stay within 'crop'
   * @param {Rectangle} crop
   */
  crop(crop) {
    crop = new Rectangle(crop)
    const positive = this.positive()
    return new Rectangle({
      top: Math.max(positive.top, crop.top || 0),
      left: Math.max(positive.left, crop.left || 0),
      right: Math.min(positive.right, crop.right || Infinity),
      bottom: Math.min(positive.bottom, crop.bottom || Infinity)
    })
  }

  /**
   * returns a new Rectangle which is not inverted
   * (bottom is below the top, left is before the right)
   */
  positive() {
    const { top, bottom, left, right } = this
    return new Rectangle({
      top: Math.min(bottom, top),
      left: Math.min(right, left),
      width: Math.abs(right - left),
      height: Math.abs(bottom - top)
    })
  }

  get style() {
    const { top, left, width, height } = this.positive()
    return { top, left, width, height }
  }

  get vCenter() {
    return this.top + this.height / 2
  }
  get hCenter() {
    return this.left + this.width / 2
  }

  overlapsH(other) {
    return this.left < other.right && this.right > other.left
  }
  overlapsV(other) {
    return this.bottom > other.top && this.top < other.bottom
  }

  isAbove(other) {
    if (this.overlapsH(other) && this.bottom < other.top) {
      return other.top - this.bottom
    }
  }

  isLeftOf(other) {
    if (this.overlapsV(other) && this.right < other.left) {
      return other.left - this.right
    }
  }
}
